/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commerical BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-20 18:26:02
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { ButtonGroup, Button, Icon, Link } from '@shopify/polaris';
import {
  MobileVerticalDotsMajor,
  EditMajor,
  DeleteMajor,
  CircleMinusMajor,
  CirclePlusMajor
} from '@shopify/polaris-icons';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import RuleCell from './rule-cell';
import NoRules from 'src/components/views/merchandising/no-rules';
import LoadingRows from 'src/components/views/merchandising/loading-rows';
import ConfirmDeleteModal from 'src/components/views/merchandising/confirm-delete-modal';

import { NUMBER_COLLECTION_DISPLAY, RANKING } from 'src/constants/merchandising';
import { reorder } from 'src/helpers/merchandising-helpers';

import { routes } from 'src/routes';
import history from 'src/routes/history';

type Props = {
  loading: boolean,
  deleting: boolean,
  rules: Array<any>,
  collectionsExpandStatus: Object,
  handleCollectionsExpand: () => void,
  handleChangeRuleStatus: () => void,
  deleteRule: () => void,
  afterDeleteRule: () => void,
  reorderRules: () => void
};

type ComposedProps = Props & WithI18nProps;
// DONE
class ListRules extends Component<ComposedProps, State> {
  state = {
    activeModalRemove: false,
    index: -1,
    selectedId: -1
  };

  componentWillReceiveProps(nextProps) {
    const { deleting, success } = nextProps;
    const { deleting: deletingPrev } = this.props;
    const { selectedId } = this.state;

    if (success === true && deleting !== deletingPrev) {
      this.handleActiveOptionModalRemove();
      this.props.afterDeleteRule(selectedId);
      this.setState({ selectedId: '' });
    }
  }

  reorder = (list, startIndex, endIndex) => {
    const updatedList = reorder(list, startIndex, endIndex);

    // Call reorder api
    this.props.reorderRules(updatedList, startIndex, endIndex);
  };

  onDragEnd = (result: DropResult) => {
    const { rules } = this.props;

    // dropped outside the list
    if (!result.destination || result.destination.index === result.source.index) {
      return;
    }

    // no movement
    if (result.destination.index === result.source.index) {
      return;
    }

    // Reorder item
    this.reorder(rules, result.source.index, result.destination.index);
  };

  render() {
    const { rules, loading, i18n } = this.props;
    const formattedRanking = RANKING.map(function (rank) {
      return {
        ...rank,
        label: i18n.translate(rank.label)
      };
    });

    return (
      <>
        <DragDropContext onDragEnd={this.onDragEnd}>
          <div className="Polaris-DataTable">
            <div className="Polaris-DataTable__ScrollContainer" style={{ marginLeft: 0 }}>
              <table className="Polaris-DataTable__Table">
                <thead>
                  <tr>
                    <th
                      className={`Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn Polaris-DataTable__Cell--header ${
                        rules.length === 0 && 'EmptyCell'
                      }`}
                    />
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Merchandising.ListRules.tableHeading1')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Merchandising.Common.ruleName')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Merchandising.Common.collections')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header">
                      {i18n.translate('AdminHTML.Merchandising.Common.ranking')}
                    </th>
                    <th className="Polaris-DataTable__Cell Polaris-DataTable__Cell--header Polaris-DataTable__Cell--numeric">
                      {i18n.translate('AdminHTML.Merchandising.ListRules.tableHeading5')}
                    </th>
                  </tr>
                </thead>
                {!_.isEmpty(rules) ? (
                  <Droppable droppableId="table">
                    {provided => (
                      <tbody ref={provided.innerRef} {...provided.droppableProps}>
                        {rules.map((item, key) => {
                          const rank =
                            _.find(formattedRanking, function (rank) {
                              return rank.value === item.ranking;
                            }) || '';
                          return (
                            // Enable drag for per option row
                            <Draggable draggableId={item.id} key={item.id} index={key}>
                              {(provided, snapshot) => (
                                <tr
                                  className="Polaris-DataTable__TableRow"
                                  ref={provided.innerRef}
                                  {...provided.dragHandleProps}
                                  {...provided.draggableProps}>
                                  {/* Drag icon */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell--firstColumn">
                                    <Icon color="subdued" source={MobileVerticalDotsMajor} />
                                  </RuleCell>

                                  {/* Status */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <label className="BC-Checkbox-Status">
                                      <input
                                        type="checkbox"
                                        checked={item.status === 'active'}
                                        onChange={e => this.updateStatus(e, item.id)}
                                      />
                                      <span />
                                    </label>
                                  </RuleCell>

                                  {/* Name */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <Link
                                      removeUnderline
                                      onClick={() => {
                                        this.handleEditRule(item.id);
                                      }}>
                                      {item.name}
                                    </Link>
                                  </RuleCell>

                                  {/* Collections */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    {this.renderCollections(item.id, item.collections)}
                                  </RuleCell>

                                  {/* Ranking */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell">
                                    <span>{rank.label}</span>
                                  </RuleCell>

                                  {/* Actions */}
                                  <RuleCell
                                    isDragOccurring={snapshot.isDragging}
                                    className="Polaris-DataTable__Cell Polaris-DataTable__Cell-Action">
                                    {this.renderAction(item.id)}
                                  </RuleCell>
                                </tr>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                        {loading && (
                          // Loading rows while reading merchandising
                          <LoadingRows />
                        )}
                      </tbody>
                    )}
                  </Droppable>
                ) : (
                  <tbody>
                    {loading && (
                      // Loading rows while reading merchandising
                      <LoadingRows />
                    )}
                  </tbody>
                )}
              </table>
              {_.isEmpty(rules) && !loading ? <NoRules /> : ''}
            </div>
          </div>
        </DragDropContext>

        <ConfirmDeleteModal
          active={this.state.activeModalRemove}
          handleChange={this.handleActiveOptionModalRemove}
          handleRemove={this.deleteRule}
          deleting={this.props.deleting}
          title={i18n.translate('AdminHTML.Merchandising.ListRules.confirmDeleteModal_Title')}
          content={i18n.translate('AdminHTML.Merchandising.ListRules.confirmDeleteModal_Content')}
        />
      </>
    );
  }

  renderCollections = (ruleId, collections) => {
    const { collectionsExpandStatus, i18n } = this.props;

    // If collections is empty, display as All collections
    if (_.isEmpty(collections)) {
      collections = [
        {
          id: 0,
          title: i18n.translate(
            'AdminHTML.Merchandising.ListRules.renderCollections_DefaultCollection_Title'
          )
        }
      ];
    }

    const viewMore = _.isArray(collections)
      ? collections.length > NUMBER_COLLECTION_DISPLAY
      : false;
    const expanded = collectionsExpandStatus[ruleId];

    let subCollections = collections || [];
    if (viewMore && !expanded) {
      subCollections = collections.slice(0, NUMBER_COLLECTION_DISPLAY);
    }

    return (
      <div>
        <ul className="collections">
          {subCollections.map(collection => {
            const { id, title } = collection;
            return title ? (
              <li key={id}>
                <span>{title}</span>
              </li>
            ) : (
              ''
            );
          })}
        </ul>
        <span className="collectionsMore">
          {viewMore ? (
            expanded ? (
              <Link onClick={() => this.props.handleCollectionsExpand(ruleId)}>
                <Icon source={CircleMinusMajor} color="base" />
                <span className="text">
                  {i18n.translate('AdminHTML.Merchandising.ListRules.link_Less')}
                </span>
              </Link>
            ) : (
              <Link onClick={() => this.props.handleCollectionsExpand(ruleId)}>
                <Icon source={CirclePlusMajor} color="base" />
                <span className="text">
                  {i18n.translate('AdminHTML.Merchandising.ListRules.link_More')}
                </span>
              </Link>
            )
          ) : (
            ''
          )}
        </span>
      </div>
    );
  };

  // Render action button for per filter
  renderAction = (ruleId: string) => {
    return (
      <ButtonGroup segmented>
        <Button size="slim" icon={EditMajor} onClick={() => this.handleEditRule(ruleId)} />
        <Button
          size="slim"
          icon={DeleteMajor}
          onClick={() => {
            this.updateSelected(ruleId);
            this.handleActiveOptionModalRemove(ruleId);
          }}
        />
      </ButtonGroup>
    );
  };

  handleEditRule = ruleId => {
    history.push(routes.merchandising.edit(ruleId));
  };

  // Update selected id
  updateSelected = ruleId => {
    this.setState(({ selectedId }) => ({
      selectedId: ruleId
    }));
  };

  // Handle open/close modal delete
  handleActiveOptionModalRemove = () => {
    this.setState(({ activeModalRemove }) => ({
      activeModalRemove: !activeModalRemove
    }));
  };

  /**
   * Active/Disable rule
   */
  updateStatus = (e, ruleId) => {
    this.props.handleChangeRuleStatus(ruleId);
  };

  /**
   * Delete rule
   */
  deleteRule = () => {
    const { selectedId } = this.state;
    this.props.deleteRule(selectedId);
  };
}

export default withI18n()(ListRules);
